import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { getOpeningHours } from '~/helpers/orchestration/customerService';
import {
  Button,
  MailIcon,
  PhoneIcon,
  TinyTitleBold,
  BodyLinkStyle,
  BodyTextStyle,
  TinyTitleRegularStyle,
  generateNameHash
} from '@kvdbil/components';
import { requestErrorHandler } from '~/helpers/notifyError';
import { useTranslation } from '~/Locale';
import { OpeningHour } from '../interfaces/OpeningHours';
import styled from 'styled-components';
import {
  formatPhoneNumber,
  formatPhoneNumberForLink
} from '~/helpers/phoneNumber';
import { BodyTextSkeleton } from '~/App/shared/components/Skeletons';
import Link from '~/App/shared/components/Link';
import { Collapse } from 'react-collapse';

dayjs.extend(calendar);

const ListItem = styled.li`
  display: flex;
  justify-content: space-between;
`;

const Cell = styled.span<{ $isExtendable?: boolean }>`
  display: flex;
  width: 100%;
  padding: 0.625rem 0;
  border-bottom: ${({ theme, $isExtendable }) =>
    $isExtendable
      ? `1px solid ${theme.colors.background.gray}`
      : `1px solid ${theme.colors.grayscaleToned.light3}`};
  align-items: center;
`;

const CellLink = styled(Cell)`
  ${BodyLinkStyle};
  gap: 1rem;
`;

const CellDate = styled(Cell)`
  ${TinyTitleRegularStyle};
`;

const CellTime = styled(Cell)`
  ${BodyTextStyle};
  justify-content: right;
`;

const CellTimeClosed = styled(CellTime)`
  color: ${({ theme }) => theme.colors.tertiary.mainAlt};
`;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
`;

const EXTENDABLE_VISIBLE_DAYS = 2;

interface Props {
  days?: number;
  phone?: string;
  email?: string;
  onlyDays?: boolean;
  isExtendable?: boolean;
}

const WeeklyCustomerService = ({
  days: amountDays,
  phone,
  email,
  onlyDays,
  isExtendable
}: Props) => {
  const { t } = useTranslation();
  const [days, setDays] = useState<OpeningHour[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const collapseIdRef = useRef(
    generateNameHash('weekly-customer-service-collapse')
  );

  useEffect(() => {
    let mounted = true;

    const currentDate = dayjs().format('YYYY-MM-DD');

    getOpeningHours(currentDate, amountDays)
      .then(data => {
        if (mounted && data?.length >= 0) {
          setDays(data);
        }
      })
      .catch(requestErrorHandler)
      .finally(() => setIsLoading(false));

    return () => {
      mounted = false;
    };
  }, [amountDays]);

  const calendarFormats = {
    sameDay: `[${t('Today')}], D/M`,
    nextDay: `[${t('Tomorrow')}], D/M`,
    nextWeek: 'dddd, D/M',
    sameElse: 'YYYY-MM-DD'
  };

  const skeletonCount = isExtendable
    ? EXTENDABLE_VISIBLE_DAYS
    : (amountDays ?? 8);

  const alwaysVisibleDays = isExtendable
    ? days?.slice(0, EXTENDABLE_VISIBLE_DAYS)
    : days;
  const collapsibleDays = isExtendable
    ? days?.slice(EXTENDABLE_VISIBLE_DAYS)
    : [];

  const canToggle =
    !!isExtendable && !isLoading && (collapsibleDays?.length ?? 0) > 0;

  const renderDay = (day: OpeningHour) => (
    <ListItem key={day.date.toISOString()}>
      <CellDate $isExtendable={isExtendable}>
        {dayjs.isDayjs(day.date)
          ? day.date.calendar(null, calendarFormats)
          : null}
      </CellDate>
      {day.type === 'CLOSED' ? (
        <CellTimeClosed $isExtendable={isExtendable}>
          {t('Customer Service Closed')}
        </CellTimeClosed>
      ) : (
        <CellTime $isExtendable={isExtendable}>
          {dayjs.isDayjs(day.open) && dayjs.isDayjs(day.close)
            ? `${day.open.format('HH:mm')} - ${day.close.format('HH:mm')}`
            : null}
        </CellTime>
      )}
    </ListItem>
  );

  const daysListItems = (
    <>
      {isLoading &&
        new Array(skeletonCount).fill('').map((_, index) => {
          return (
            <ListItem key={`skeleton-${index}`}>
              <Cell $isExtendable={isExtendable}>
                <BodyTextSkeleton height={1.5} style={{ width: '100%' }} />
              </Cell>
            </ListItem>
          );
        })}

      {!isLoading && alwaysVisibleDays?.map(renderDay)}

      {!isLoading && isExtendable && !!collapsibleDays?.length && (
        <div
          id={collapseIdRef.current}
          role="region"
          aria-label={t('Customer Service')}
          aria-hidden={!isExpanded}
        >
          <Collapse isOpened={isExpanded}>
            {collapsibleDays.map(renderDay)}
          </Collapse>
        </div>
      )}

      {canToggle && (
        <ToggleWrapper>
          <Button
            onClick={() => setIsExpanded(prev => !prev)}
            variant="flat"
            color="info"
            colorShade="dark"
            aria-expanded={isExpanded}
            aria-controls={collapseIdRef.current}
            aria-label={
              isExpanded
                ? t('Hide additional opening hours')
                : t('Show additional opening hours')
            }
          >
            {isExpanded ? t('Minimize') : t('Show more')}
          </Button>
        </ToggleWrapper>
      )}
    </>
  );

  if (onlyDays) {
    return daysListItems;
  }

  return (
    <div>
      <TinyTitleBold>{t('Customer Service')}</TinyTitleBold>
      <div>
        <ul>
          <ListItem>
            <CellLink>
              <PhoneIcon style={{ fontSize: '1.5rem' }} />
              <Link
                to={`tel:${formatPhoneNumberForLink(
                  phone ?? t('CUSTOMER_CONTACT_PHONE')
                )}`}
                withUnderline
              >
                {formatPhoneNumber(phone ?? t('CUSTOMER_CONTACT_PHONE'))}
              </Link>
            </CellLink>
          </ListItem>

          <ListItem>
            <CellLink>
              <MailIcon style={{ fontSize: '1.5rem' }} />
              <Link
                to={`mailto:${email ?? t('CUSTOMER_CONTACT_MAIL')}`}
                withUnderline
              >
                {email ?? t('CUSTOMER_CONTACT_MAIL')}
              </Link>
            </CellLink>
          </ListItem>

          {daysListItems}
        </ul>
      </div>
    </div>
  );
};

export default WeeklyCustomerService;
